import { FiTag, FiCheck, FiEdit3 } from "react-icons/fi";
import { toast } from "react-toastify";

const AdjustmentAmountCard = ({
  adjustmentAmount,
  adjustmentReason,
  onAdjustmentChange,
  onAdjustmentReasonChange,

  /** Pre-adjustment total used for the strikethrough preview */
  orderTotal = 0,

  /** Optional cap. Pass totalSummary.total in Create, grand_total in Edit */
  maxAmount = null,

  /** Show the Save button (Edit mode only) */
  showSaveButton = false,
  onSave,
  saving = false,

  /** Persisted value – disables Save when unchanged (Edit mode) */
  savedValue = 0,

  /** Persisted reason – disables Save when unchanged (Edit mode) */
  savedReason = "",

  /** Disable the whole control */
  disabled = false,

  className = "",
}) => {
  const adjustment = parseFloat(adjustmentAmount || 0) || 0;
  const originalTotal = parseFloat(orderTotal || 0) || 0;
  const finalAmount = Math.max(0, originalTotal - adjustment);

  const hasAmountChanged =
    adjustment !== (parseFloat(savedValue || 0) || 0);
  const hasReasonChanged =
    (adjustmentReason || "").trim() !== (savedReason || "").trim();

  const isSaveDisabled =
    saving || disabled || (!hasAmountChanged && !hasReasonChanged);

  const handleChange = (e) => {
    const val = e.target.value;
    if (val === "") {
      onAdjustmentChange(0);
      return;
    }
    const num = parseFloat(val);
    if (isNaN(num) || num < 0) return;

    if (maxAmount !== null && num > maxAmount) {
      toast.warn(
        `Adjustment cannot exceed order amount (₹${maxAmount.toFixed(2)})`,
        { position: "top-center" },
      );
      return;
    }
    onAdjustmentChange(num);
  };

  const handleSave = () => {
    if (isSaveDisabled) return;
    onSave?.();
  };

  return (
    <div
      className={`p-2 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl ${className}`}
    >
      {/* Single-line row: label + amount + reason + save */}
      <div className="flex items-center gap-2">
        {/* Label / icon */}
        <span className="flex items-center gap-1 text-xs font-semibold text-gray-700 whitespace-nowrap flex-shrink-0">
          <FiTag className="w-4 h-4 text-amber-600" />
          Adjustment
        </span>

        {/* Amount input */}
        <div className="relative w-28 flex-shrink-0">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">
            ₹
          </span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={adjustmentAmount || ""}
            disabled={disabled || saving}
            onChange={handleChange}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
            }}
            placeholder="0.00"
            className="w-full pl-6 pr-2 py-1.5 text-sm border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>

        {/* Reason input */}
        <div className="relative flex-1 min-w-0">
          <FiEdit3 className="absolute left-2 top-1/2 -translate-y-1/2 text-amber-600 w-3.5 h-3.5" />
          <input
            type="text"
            value={adjustmentReason || ""}
            disabled={disabled || saving}
            onChange={(e) => onAdjustmentReasonChange?.(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
            }}
            maxLength={200}
            placeholder="Reason for adjustment (optional)"
            className="w-full pl-7 pr-2 py-1.5 text-sm border border-amber-300 rounded-lg bg-white placeholder:text-gray-400 focus:ring-2 focus:ring-amber-500 focus:border-amber-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>

        {/* Save button (edit mode) */}
        {showSaveButton && (
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaveDisabled}
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex-shrink-0"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white" />
                Saving
              </>
            ) : (
              <>
                <FiCheck className="w-3.5 h-3.5" />
                Save
              </>
            )}
          </button>
        )}
      </div>

      {/* Final amount preview on its own line */}
      {adjustment > 0 && (
        <div className="mt-2 flex justify-between items-center border-t border-amber-200 pt-1.5">
          <span className="text-sm font-semibold text-gray-700">
            Updated Order Amount
          </span>
          <div className="flex items-baseline gap-2">
            <span className="text-sm text-gray-400 line-through">
              ₹ {originalTotal.toFixed(0)}
            </span>
            <span className="text-xl font-bold text-amber-700">
              ₹ {finalAmount.toFixed(0)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdjustmentAmountCard;